import styles from "./DispatchesPage.module.scss";
import type { DispatchStatus, DispatchValidationStatus } from "./types";

/**
 * Shared dispatch status catalog: English key -> Spanish label.
 * UI order matches the backend DISPATCH_STATUSES order; labels are product-exact.
 */
export const statusLabels: Record<DispatchStatus, string> = {
  order_preparation: "Prealistamiento de pedido",
  ready_in_warehouse: "Listo en bodega",
  dispatched: "Despachado",
  product_picked: "Producto separado",
  maritime_transit: "Transito naviero",
  internal_warehouse_issue: "Novedad bodega interno",
  partial_delivery: "Entrega parcial",
};

/** Figma badge class per status (visual only, no model change) */
export const statusBadgeClass: Record<DispatchStatus, string> = {
  order_preparation: styles.statusPending,
  ready_in_warehouse: styles.statusApproved,
  dispatched: styles.statusShipped,
  product_picked: styles.statusInPreparation,
  maritime_transit: styles.statusTransit,
  internal_warehouse_issue: styles.statusIssue,
  partial_delivery: styles.statusPartial,
};

/**
 * Validation catalog: English key -> Spanish label.
 * UI order follows the backend DISPATCH_VALIDATION_STATUSES catalog.
 */
export const validationLabels: Record<DispatchValidationStatus, string> = {
  pending: "Pendiente",
  reserved: "Reservado",
  approved: "Aprobado",
  rejected: "Rechazado",
};

/** Figma badge class per validation state (same palette as the status badges) */
export const validationBadgeClass: Record<DispatchValidationStatus, string> = {
  pending: styles.validationPending,
  reserved: styles.validationReserved,
  approved: styles.validationApproved,
  rejected: styles.validationRejected,
};

/** History actions: lifecycle actions plus every status key (matches backend DISPATCH_HISTORY_ACTIONS) */
export const actionLabels: Record<string, string> = {
  created: "Creado",
  edited: "Editado",
  approved: "Aprobado",
  rejected: "Rechazado",
  ...statusLabels,
};

/**
 * Statuses that still accept edits — pre-dispatch frontier (rule 14-oct-2026):
 * `dispatched` means the dispatch is finished, so that status and every status
 * after it are read-only in the grid. Mirrors the backend source of truth in
 * `server/apiv2/utils/dispatchUtils.js` (`EDITABLE_STATUSES`); keep both in sync.
 */
export const EDITABLE_STATUSES: DispatchStatus[] = [
  "order_preparation",
  "ready_in_warehouse",
];

/** Whether the «Editar» action is available for a dispatch in this status */
export const isEditableStatus = (status: DispatchStatus): boolean =>
  EDITABLE_STATUSES.includes(status);
